import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Clock, User, Calendar, ChevronRight } from 'lucide-react';
import { db } from '@/lib/firebase/config';
import { collection, getDocs } from 'firebase/firestore';
import { LandingNav } from '@/components/landing/LandingNav';
import { LandingFooter } from '@/components/landing/LandingFooter';
import { useLanguage } from '@/contexts/LanguageContext';
import { toArabicNumerals } from '@/lib/utils';

interface Article {
  id: string;
  title: { ar: string; en: string; } | string;
  excerpt: { ar: string; en: string; } | string;
  content: { ar: string; en: string; } | string;
  author: { ar: string; en: string; } | string;
  category: { ar: string; en: string; } | string;
  image_url: string;
  read_time: number;
  created_at: string;
}

export function LandingArticleDetailPage() {
  const { id } = useParams();
  const [article, setArticle] = useState<Article | null>(null);
  const [relatedArticles, setRelatedArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const { language, t } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    fetchArticle();
  }, [id]);

  const generateArticleHash = (articleId: string) => {
    return btoa(articleId).substring(0, 8);
  };

  const fetchArticle = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'landing_articles'));

      const articlesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        created_at: doc.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString()
      }));

      const matchingArticle = articlesData.find(article => 
        generateArticleHash(article.id) === id
      ) as Article | undefined;

      if (matchingArticle) {
        setArticle(matchingArticle);
        
        // Get related articles
        const related = articlesData
          .filter(a => a.id !== matchingArticle.id)
          .slice(0, 5) as Article[];
        setRelatedArticles(related);
      }
    } catch (err) {
      console.error('Error fetching article:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: string) => {
    const formatted = new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    // Convert to Arabic-Indic numerals if in Arabic mode
    return language === 'ar' ? toArabicNumerals(formatted) : formatted;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white" dir={language === 'ar' ? 'rtl' : 'ltr'}>
        <LandingNav />
        <div className="pt-28 md:pt-32 pb-8 md:pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">{t('landing.common.loading')}</div>
          </div>
        </div>
        <LandingFooter />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-white" dir={language === 'ar' ? 'rtl' : 'ltr'}>
        <LandingNav />
        <div className="pt-28 md:pt-32 pb-8 md:pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center text-red-600">{t('landing.articles.articleNotFound')}</div>
          </div>
        </div>
        <LandingFooter />
      </div>
    );
  }

  // Safe rendering for bilingual fields
  const title = typeof article.title === 'string' 
    ? article.title 
    : (article.title[language] || article.title.ar || article.title.en || '');
  const excerpt = typeof article.excerpt === 'string' 
    ? article.excerpt 
    : (article.excerpt[language] || article.excerpt.ar || article.excerpt.en || '');
  const content = typeof article.content === 'string' 
    ? article.content 
    : (article.content[language] || article.content.ar || article.content.en || '');
  const author = typeof article.author === 'string' 
    ? article.author 
    : (article.author[language] || article.author.ar || article.author.en || '');
  const category = typeof article.category === 'string' 
    ? article.category 
    : (article.category[language] || article.category.ar || article.category.en || '');

  return (
    <div className="min-h-screen bg-white" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <LandingNav />
      <div className="pt-28 md:pt-32 pb-8 md:pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-8">
            <article className="lg:w-2/3">
              <header className="mb-6 md:mb-8 pt-4 md:pt-0">
                <div className="mb-4 md:mb-6">
                  <span className="bg-[#7dda55] text-white px-3 py-1 rounded-full text-sm">
                    {category}
                  </span>
                </div>
                <h1 className="text-2xl md:text-4xl font-bold text-[#004250] mb-6 md:mb-8">
                  {title}
                </h1>
                <div className="flex flex-wrap gap-4 md:gap-6 text-sm md:text-base text-gray-600">
                  <div className="flex items-center gap-1 md:gap-2">
                    <User className="w-4 h-4 md:w-5 md:h-5" />
                    <span>{author}</span>
                  </div>
                  <div className="flex items-center gap-1 md:gap-2">
                    <Calendar className="w-4 h-4 md:w-5 md:h-5" />
                    <span>{formatDate(article.created_at)}</span>
                  </div>
                  <div className="flex items-center gap-1 md:gap-2">
                    <Clock className="w-4 h-4 md:w-5 md:h-5" />
                    <span>{article.read_time} {t('landing.articles.minRead')}</span>
                  </div>
                </div>
              </header>

              <div className="mb-8 md:mb-12">
                <img
                  src={article.image_url}
                  alt={title}
                  className="w-full h-[200px] md:h-[400px] object-cover rounded-lg shadow-lg"
                />
              </div>

              <div className="prose prose-sm md:prose-lg max-w-none">
                <p className="text-lg md:text-xl text-gray-600 mb-6 md:mb-8 font-medium">
                  {excerpt}
                </p>
                <div className="text-gray-800 leading-relaxed space-y-4 md:space-y-6 whitespace-pre-line text-base md:text-lg">
                  {content}
                </div>
              </div>
            </article>

            <aside className="lg:w-1/3">
              <div className="bg-gray-50 rounded-lg p-4 md:p-6 sticky top-24">
                <h2 className="text-lg md:text-xl font-bold text-[#004250] mb-4 md:mb-6">
                  {t('landing.articles.otherArticles')}
                </h2>
                <div className="space-y-4 md:space-y-6">
                  {relatedArticles.map((relatedArticle) => {
                    // Safe rendering for related article bilingual fields
                    const relatedTitle = typeof relatedArticle.title === 'string' 
                      ? relatedArticle.title 
                      : (relatedArticle.title[language] || relatedArticle.title.ar || relatedArticle.title.en || '');
                    
                    return (
                      <Link
                        key={relatedArticle.id}
                        to={`/landing/article/${generateArticleHash(relatedArticle.id)}`}
                        className="block group"
                      >
                        <article className="flex gap-3 md:gap-4 items-start">
                          <img
                            src={relatedArticle.image_url}
                            alt={relatedTitle}
                            className="w-20 h-20 md:w-24 md:h-24 object-cover rounded-lg flex-shrink-0"
                          />
                          <div>
                            <h3 className="font-semibold text-sm md:text-base text-[#004250] group-hover:text-[#7dda55] transition-colors line-clamp-2">
                              {relatedTitle}
                            </h3>
                            <div className="text-xs md:text-sm text-gray-500 mt-2">
                              {formatDate(relatedArticle.created_at)}
                            </div>
                          </div>
                        </article>
                      </Link>
                    );
                  })}
                </div>
                <div className="mt-6 md:mt-8">
                  <Link
                    to="/landing"
                    className="flex items-center justify-center gap-2 text-[#004250] hover:text-[#7dda55] transition-colors text-sm md:text-base"
                  >
                    <ChevronRight className="w-4 h-4" />
                    <span>{t('landing.articles.backToHome')}</span>
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
      <LandingFooter />
    </div>
  );
}

